import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { paymentApi } from '../services/paymentApi';
import { creditNoteKeys } from './useCreditNotes';
import { subcontractKeys } from './useSubcontractAssignments';
import { useInvalidateWorkOrder } from './useWorkOrders';
import type { RefundPaymentRequest, StorePaymentRequest } from '../types/service.types';

export const paymentKeys = {
  all: ['payments'] as const,
  lists: () => [...paymentKeys.all, 'list'] as const,
  list: (workOrderId: string) => [...paymentKeys.lists(), workOrderId] as const,
  details: () => [...paymentKeys.all, 'detail'] as const,
  detail: (id: string) => [...paymentKeys.details(), id] as const,
};

export function usePaymentsForWorkOrder(workOrderId: string) {
  return useQuery({
    queryKey: paymentKeys.list(workOrderId),
    queryFn: () => paymentApi.listForWorkOrder(workOrderId),
    enabled: !!workOrderId,
  });
}

export function usePayment(id: string) {
  return useQuery({
    queryKey: paymentKeys.detail(id),
    queryFn: () => paymentApi.get(id),
    enabled: !!id,
  });
}

/**
 * D8-05: create-and-read only — no update, no delete. Recording a payment
 * changes the WO's `balance_due_cents`/`invoice_state`, so it invalidates
 * the parent work order like every other Service mutation.
 *
 * 18-UAT gap 1 (#408, 18-21): a payment also moves every sublet's
 * `customer_payment_cover` (D18-05 / D18-19 — deposits count), so the work
 * order's sublet assignment list is invalidated too; otherwise a Settle button
 * disabled on a shortfall stayed disabled until a reload.
 */
export function useRecordPayment(workOrderId: string) {
  const invalidateWorkOrder = useInvalidateWorkOrder();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: StorePaymentRequest) => paymentApi.record(workOrderId, data),
    onSuccess: () => {
      invalidateWorkOrder(workOrderId);
      queryClient.invalidateQueries({ queryKey: subcontractKeys.list(workOrderId) });
    },
  });
}

/**
 * D14-12/D14-17: a refund changes the payment log (this WO's `payments`
 * list), the work-order totals (`useInvalidateWorkOrder()`) and — when it
 * settles a credit note — the customer's open credit on every other work
 * order too, so all three query sets are invalidated. The refund row is a
 * `payments` write, not a `credit_notes` write, so this hook lives beside
 * the other payment mutations rather than in `useCreditNotes.ts`. A refund
 * lowers the sublet cover as well (18-UAT gap 1), so the sublet list is the
 * fourth query set.
 */
export function useRefundPayment(workOrderId: string) {
  const invalidateWorkOrder = useInvalidateWorkOrder();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ paymentId, data }: { paymentId: string; data: RefundPaymentRequest }) =>
      paymentApi.refund(paymentId, data),
    onSuccess: () => {
      invalidateWorkOrder(workOrderId);
      queryClient.invalidateQueries({ queryKey: paymentKeys.list(workOrderId) });
      queryClient.invalidateQueries({ queryKey: creditNoteKeys.customerLists() });
      queryClient.invalidateQueries({ queryKey: subcontractKeys.list(workOrderId) });
    },
  });
}
